import React, { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MessageSquare, Clock, Pencil, Trash2, Check, X } from "lucide-react";
import { ConfirmDialog } from "./ui/ConfirmDialog";

export const ConversationsList = ({
  conversations,
  totalConversations,
  currentConversationId,
  onSelectConversation,
  onNewConversation,
  onRenameConversation,
  onDeleteConversation,
  isLoadingHistory,
  hasMoreConversations,
  isLoadingMoreConversations,
  onLoadMore,
}) => {
  const { t } = useTranslation();
  const observerTarget = useRef(null);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    id: null,
    title: "",
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMoreConversations &&
          !isLoadingMoreConversations &&
          !isLoadingHistory
        ) {
          onLoadMore();
        }
      },
      { threshold: 1.0 },
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [
    hasMoreConversations,
    isLoadingMoreConversations,
    isLoadingHistory,
    onLoadMore,
  ]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t("time.justNow", "Just now");
    if (diffMins < 60)
      return t("time.minutesAgo", `${diffMins} min ago`, { count: diffMins });
    if (diffHours < 24)
      return t("time.hoursAgo", `${diffHours}h ago`, { count: diffHours });
    if (diffDays < 7)
      return t("time.daysAgo", `${diffDays}d ago`, { count: diffDays });
    return date.toLocaleDateString();
  };

  const handleStartEdit = (conv) => {
    setEditingId(conv.conversation_id);
    setEditTitle(conv.title || "");
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditTitle("");
  };

  const handleSaveEdit = (conversationId) => {
    if (editTitle.trim() && onRenameConversation) {
      onRenameConversation(conversationId, editTitle.trim());
      setEditingId(null);
      setEditTitle("");
    }
  };

  const handleDeleteClick = (conversationId, title) => {
    setDeleteDialog({
      isOpen: true,
      id: conversationId,
      title: title || t("conversations.untitled", "Untitled conversation"),
    });
  };

  const handleDeleteConfirm = () => {
    if (deleteDialog.id && onDeleteConversation) {
      onDeleteConversation(deleteDialog.id);
    }
    setDeleteDialog({ isOpen: false, id: null, title: "" });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ isOpen: false, id: null, title: "" });
  };

  return (
    <div className="gpc-conversations-container">
      {/* Header */}
      <div className="gpc-conversations-header">
        <h3 className="gpc-conversations-title">
          {t("conversations.title", "Conversations")}
        </h3>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onNewConversation();
          }}
          className="gpc-new-conversation-button gpc-focus-visible gpc-transition"
          disabled={isLoadingHistory}
          aria-label={t("conversations.new", "New")}
        >
          <MessageSquare size={16} />
          <span>{t("conversations.new", "New")}</span>
        </button>
      </div>

      {/* Conversations List */}
      <div className="gpc-conversations-list">
        {conversations.length === 0 ? (
          <div className="gpc-conversations-empty">
            <MessageSquare size={48} className="gpc-conversations-empty-icon" />
            <p className="gpc-conversations-empty-text">
              {t("conversations.empty", "No conversations yet")}
            </p>
            <p className="gpc-conversations-empty-hint">
              {t(
                "conversations.emptyHint",
                "Start a conversation to see it here",
              )}
            </p>
          </div>
        ) : (
          <div className="gpc-conversations-items">
            {conversations.map((conv) => (
              <div
                key={conv.conversation_id}
                className={`gpc-conversation-item gpc-animate-fade-in ${
                  currentConversationId === conv.conversation_id
                    ? "gpc-conversation-item-active"
                    : ""
                } ${isLoadingHistory ? "gpc-conversation-item-disabled" : ""}`}
              >
                <div
                  className="gpc-conversation-content gpc-transition"
                  onClick={() =>
                    editingId !== conv.conversation_id &&
                    !isLoadingHistory &&
                    onSelectConversation(conv.conversation_id)
                  }
                >
                  {editingId === conv.conversation_id ? (
                    <div className="gpc-conversation-edit">
                      <input
                        type="text"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter")
                            handleSaveEdit(conv.conversation_id);
                          if (e.key === "Escape") handleCancelEdit();
                        }}
                        className="gpc-conversation-edit-input gpc-focus-visible"
                        placeholder={t(
                          "conversations.untitled",
                          "Untitled conversation",
                        )}
                        autoFocus
                        maxLength={200}
                      />
                      <div className="gpc-conversation-edit-actions">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleSaveEdit(conv.conversation_id);
                          }}
                          className="gpc-conversation-action-button gpc-conversation-action-save gpc-focus-visible gpc-transition"
                          title={t("conversations.save", "Save")}
                          aria-label={t("conversations.save", "Save")}
                        >
                          <Check size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleCancelEdit();
                          }}
                          className="gpc-conversation-action-button gpc-conversation-action-cancel gpc-focus-visible gpc-transition"
                          title={t("conversations.cancel", "Cancel")}
                          aria-label={t("conversations.cancel", "Cancel")}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="gpc-conversation-header">
                        <h4 className="gpc-conversation-title">
                          {conv.title ||
                            t(
                              "conversations.untitled",
                              "Untitled conversation",
                            )}
                        </h4>
                        <div className="gpc-conversation-actions">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleStartEdit(conv);
                            }}
                            className="gpc-conversation-action-button gpc-focus-visible gpc-transition"
                            disabled={isLoadingHistory}
                            title={t("conversations.rename", "Rename")}
                            aria-label={t("conversations.rename", "Rename")}
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDeleteClick(
                                conv.conversation_id,
                                conv.title ||
                                  t(
                                    "conversations.untitled",
                                    "Untitled conversation",
                                  ),
                              );
                            }}
                            className="gpc-conversation-action-button gpc-conversation-action-delete gpc-focus-visible gpc-transition"
                            disabled={isLoadingHistory}
                            title={t("conversations.delete", "Delete")}
                            aria-label={t("conversations.delete", "Delete")}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>

                      <div className="gpc-conversation-metadata">
                        <div className="gpc-conversation-meta-item">
                          <MessageSquare size={12} />
                          <span>{conv.total_messages}</span>
                        </div>

                        <div className="gpc-conversation-meta-item">
                          <Clock size={12} />
                          <span>{formatDate(conv.last_modification_date)}</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}

            {/* Loading indicator y trigger para scroll infinito */}
            {isLoadingMoreConversations && (
              <div className="gpc-conversations-loading">
                <div className="gpc-loading-spinner"></div>
                <span>{t("conversations.loadingMore", "Loading more...")}</span>
              </div>
            )}

            {/* Elemento observador para scroll infinito */}
            {hasMoreConversations && !isLoadingMoreConversations && (
              <div ref={observerTarget} style={{ height: "1px" }} />
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Toast */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        message={t("conversations.confirmDelete", {
          title: deleteDialog.title,
          defaultValue: `Delete "${deleteDialog.title}"?`,
        })}
        confirmText={t("conversations.delete", "Delete")}
        cancelText={t("conversations.cancel", "Cancel")}
        variant="danger"
      />
    </div>
  );
};
